import { Chip } from 'components';
import { useCallback, useMemo } from 'react';
import { TQuickReplyItem } from 'types';

/** Default = existing orange chip. `premium` = black × pale gold (Issue #2775). */
const CHIP_TONE_CLASSES = {
  default: {
    bgColor: 'bg-primary-50',
    textColor: 'text-primary',
    ringColor: 'ring-primary',
  },
  premium: {
    bgColor: 'bg-neutral-950',
    textColor: 'text-primary-200',
    ringColor: 'ring-primary-200',
  },
} as const;

type Props = {
  items: TQuickReplyItem[];
  disabled?: boolean;
  onSelect: (item: TQuickReplyItem) => void;
};

const QuickReplyChips = ({ items, disabled = false, onSelect }: Props) => {
  const chipClassName = useMemo(
    () =>
      `shrink-0 font-semibold ${
        disabled
          ? 'opacity-50 pointer-events-none'
          : 'cursor-pointer active:opacity-80'
      }`,
    [disabled],
  );

  const handleSelect = useCallback(
    (item: TQuickReplyItem) => {
      if (!disabled) onSelect(item);
    },
    [disabled, onSelect],
  );

  if (!items.length) return null;

  return (
    <div
      className="flex shrink-0 gap-2 overflow-x-auto px-4 pt-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      data-testid="quick-reply-chips"
    >
      {items.map((item) => (
        <Chip
          key={item.id}
          onClick={() => handleSelect(item)}
          {...CHIP_TONE_CLASSES[item.tone ?? 'default']}
          rounded="rounded-full"
          padding="py-[8px] px-[14px]"
          textSize="text-[13px] whitespace-nowrap"
          className={chipClassName}
        >
          {item.suggestText}
        </Chip>
      ))}
    </div>
  );
};

export default QuickReplyChips;
